import { IKitDropdownRef } from '@models/IKitDropdownRef';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import { IProductVariant } from '@models/IProductVariant';
import KitCard from '@molecules/KitCard/KitCard';
import NavigationCarousel from '@molecules/NavigationCarousel/NavigationCarousel';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props {
  readonly kitTitleLabel: string;
  readonly kitsTitleLabel: string;
  readonly productListingName?: string;
  readonly statusLabels: IProductListingStatusLabels;
  readonly variations: IProductVariant[];
  readonly isLightMode?: boolean;
}

const KitDropdown = forwardRef<IKitDropdownRef, Props>(
  (
    {
      kitTitleLabel,
      kitsTitleLabel,
      productListingName,
      statusLabels,
      variations = [],
      isLightMode,
    }: Props,
    ref
  ) => {
    const isMobile = useMediaQuery(768);
    const sectionRef = useRef<HTMLDivElement | null>(null);
    const kits =
      variations?.filter((variation) => variation.type === 'kit') ?? [];

    useImperativeHandle(ref, () => ({
      scrollIntoView: () => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      },
    }));

    return (
      <section
        ref={sectionRef}
        className={classNames(
          'mt-4 py-15 md:pt-24 md:pb-36',
          isLightMode ? 'bg-grey-100 text-black-100' : 'bg-grey-600 text-white'
        )}
      >
        <div>
          {kitsTitleLabel && kitTitleLabel && (
            <h4 className="container mb-5 md:mb-6">
              {`${kits.length} ${
                kits.length > 1 ? kitsTitleLabel : kitTitleLabel
              }`}
            </h4>
          )}

          {!!kits.length && isMobile && (
            <NavigationCarousel
              id="kit-dropdown-mobile-carousel"
              shadowColour={isLightMode ? 'from-grey-100' : 'from-grey-600'}
              headerWrapperDefaultJustifyClassName="justify-start"
              items={kits}
              loop={false}
              shadows
              spaceBetween={16}
              slidesToShowMobile={kits.length === 1 ? 1 : 1.1}
              disabledNavButtonArrowColor={isLightMode ? '' : 'text-grey-600'}
              disabledNavButtonBackgroundColor={
                isLightMode ? '' : 'bg-black-100'
              }
            >
              {(itemsToDisplay) =>
                itemsToDisplay.map((item) => (
                  <SwiperSlide
                    className="stretch-card"
                    key={`${item.skuCode}-${item.title}-kit-card`}
                  >
                    <KitCard
                      productListingName={productListingName}
                      statusLabels={statusLabels}
                      {...item}
                      isLightMode={isLightMode}
                    />
                  </SwiperSlide>
                ))
              }
            </NavigationCarousel>
          )}

          {!!kits.length && !isMobile && (
            <div className="container grid w-full grid-cols-2 gap-x-4 gap-y-22 xl:grid-cols-3">
              {kits.map((kit) => (
                <div
                  key={`${kit.skuCode}-${kit.title}-kit-card`}
                  className="w-full shrink-0"
                >
                  <KitCard
                    productListingName={productListingName}
                    statusLabels={statusLabels}
                    {...kit}
                    isLightMode={isLightMode}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }
);

export default KitDropdown;
